import React, { useEffect, useMemo, useState } from 'react';
import { useLaneRateCardsHistory } from '@api';
import {
	BUTTON_TYPES,
	CHIP_SIZE,
	CHIP_TYPE,
	RateCardHistory,
	RateCardHistoryItem,
	SettingType,
	TOOLTIP_POSITION
} from '@types';
import {
	Button,
	Label,
	Loader,
	Spacer,
	StatusChip,
	Text,
	Tooltip
} from '@components';
import {
	DATE_FORMATS,
	formatDate,
	formatIsoDateTimeToSydneyDate,
	formatIsoDateTimeToSydneyTime
} from '@utils';
import toast from 'react-hot-toast';
import { useWithImpersonation } from '@hooks';
import styles from './history.module.scss';
import { useNavigate } from 'react-router-dom';
import { CircleIcon } from '@img/icons/CircleIcon';

interface ShipperRateCardHistoryProps {
	laneData: string;
	onClose: () => void;
	type: SettingType
	isLinkedRateCard?: boolean;
}
export const ShipperRateCardHistory = ({
	laneData,
	onClose,
	type,
	isLinkedRateCard = false,
}: ShipperRateCardHistoryProps) => {
	const {
		isLoadingGetLane,
		callGetHistory
	} = useLaneRateCardsHistory();
	const [allLanes, setAllLanes] = useState<RateCardHistory | null>(null);
	const historyItems = useMemo(() => {
		if (!allLanes) return [];
		return allLanes.data;
	}, [allLanes]);
	const historicalLink = type === 'Admin' ? 'configuration' : type.toLowerCase();
	const groupedHistory = useMemo(() => {
		return historyItems.reduce((groups: { [date: string]: RateCardHistoryItem[] }, item: RateCardHistoryItem, index: number) => {
			const date = formatIsoDateTimeToSydneyDate(item.actioned_at);

			if (index === 0 && item.action.toLowerCase() === 'updated') {
				item.action = 'Current';
			}
			const key = `${date}-${item.action}`;

			if (!groups[key]) {
				groups[key] = [];
			}
			groups[key].push(item);
			return groups;
		}, {});
	}, [historyItems]);

	const navigate = useNavigate();

	type impersonationRole = 'shipper' | 'carrier';
	const dynamicType = type === 'Admin' ? 'shipper' : type.toLowerCase();
	const withImpersonation = useWithImpersonation(dynamicType as impersonationRole, isLinkedRateCard)
	// Doesnt need impersonation with admin - only accepts shipper and carrier - cant conditionally call useWithImpersonation
	const [hasRevisions, setHasRevisions] = useState<boolean>(false);

	const displayChip = (action: string): CHIP_TYPE => {
		switch (action) {
		case 'Updated':
			return CHIP_TYPE.WARN;
		case 'Created':
			return CHIP_TYPE.SUCCESS;
		case 'Current':
			return CHIP_TYPE.NEUTRAL;
		default: // deleted
			return CHIP_TYPE.ERROR;
		}
	}

	useEffect(() => {
		if (historyItems) {
			setHasRevisions(historyItems.length > 1);
		}
	}, [historyItems]);

	useEffect(() => {
		callGetHistory({ routes: [laneData] })
			.then((response) => {
				if (response && response.data) {
					setAllLanes(response.data);
				}
			})
			.catch((error) => {
				toast.error('Error fetching lane history');
				onClose();
				console.error('Error fetching lane history:', error);
			});
	}, [laneData]);
	return (
		<div>
			{isLoadingGetLane && <Loader/>}
			{!isLoadingGetLane && (
				<React.Fragment>

					<>
						<Tooltip visible={!hasRevisions} position={TOOLTIP_POSITION.TOP} text={'No previous versions yet'}>
							<Button
								onClick={() => navigate(withImpersonation(`/${historicalLink}/ratecards/${laneData}/history/`))}
								text={'Launch Version History Tool'} type={BUTTON_TYPES.SECONDARY}
								disabled={!hasRevisions}/>
						</Tooltip>
						<Spacer size={20} divider/>
						{groupedHistory && Object.entries(groupedHistory).map(([_, items], index) => (
							<React.Fragment key={`grouped-${index}`}>
								<div className={styles.container} key={`history-${index}`}>

									<div className={styles.item}>
										<Label
											text={formatDate(new Date(items[0].actioned_at), DATE_FORMATS.DATE_WITH_SHORT_MONTH_NAME)}/>
										<StatusChip text={items[0].action} type={displayChip(items[0].action)} size={CHIP_SIZE.MEDIUM}/>
									</div>

									<Spacer size={10}/>

									{items.map((item) => (
										<React.Fragment key={item.external_id}>
											<div
												className={`${styles.item} ${items.length > 1 ? styles.container : ''}`}>

												<Text>{formatIsoDateTimeToSydneyTime(item.actioned_at)}</Text>
												<CircleIcon />
												<Text>{item.user.name || '-'}</Text>
												{
													item.description && <>
														<CircleIcon />
														<Text>{item.description}</Text>
													</>
												}

											</div>
											<Spacer size={10}/>
										</React.Fragment>
									))}

								</div>
								<Spacer size={20}/>
							</React.Fragment>
						))}
					</>
				</React.Fragment>
			)}
			<div>
				Showing {allLanes?.data.length} of {allLanes?.meta.total ?? 0} entries.
			</div>
		</div>
	);
};